import { useEffect, useState, useRef, startTransition, forwardRef } from 'react'
import {
  NavigationNestedItem,
  NavigationLinkItem,
  SanityImageMediaPayload,
  SiteConfiguration,
} from '@portfolio/models'
import { styled, keyframes } from '@portfolio/styles'
import {
  Button,
  Container,
  Link,
  Modal,
  AnnouncementBar,
  Media,
  isLinkHref,
} from '@portfolio/components'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/dist/client/router'
import { Chevron, Logo } from '@portfolio/icons'
import { MobileDrawer } from './MobileDrawer'
import { NestedNavigationPanel } from './components/NestedNavigationPanel'

const collapseHeight = keyframes({
  '0%': {
    height: 'calc($headerHeight + $nestedNavHeight)',
  },
  '100%': {
    height: '$headerHeight',
  },
})

type Props = {
  header: SiteConfiguration['header']
  theme: 'transparent-dark' | 'transparent-light'
  position: 'fixed' | 'absolute'
  announcementBar: SiteConfiguration['announcementBar']
}

const StyledWrapper = styled('div', {
  zIndex: '$header',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
})

const StyledContainer = styled(Container, {
  height: '$headerHeight',
  projectFont: 'body03',
  display: 'flex',
  gap: '$24',
  transition: 'background-color 0.2s ease, backdrop-filter 0.2s ease',
  position: 'relative',

  '@lg': {
    '&:has(.nested-navigation-container[data-open="true"])': {
      alignItems: 'flex-start',
      backdropFilter: 'blur(30px)',
      height: 'calc($headerHeight + $nestedNavHeight)',
    },

    '&:not(:has(.nested-navigation-container[data-open="true"]))': {
      animation: `${collapseHeight} 0.2s ease`,
    },
  },

  variants: {
    theme: {
      'transparent-dark': {
        color: '$black',

        '@lg': {
          '&:has(.nested-navigation-container[data-open="true"])': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
          },
        },
      },
      'transparent-light': {
        color: '$white',

        '@lg': {
          '&:has(.nested-navigation-container[data-open="true"])': {
            backgroundColor: 'rgba(18, 18, 18, 0.8)',
          },
        },
      },
    },

    position: {
      absolute: {
        position: 'absolute',
      },
      fixed: {
        position: 'fixed',
      },
    },
  },
})

const NavContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '$headerHeight',
  width: '100%',
  maxWidth: '$maxNavigationWidth',
  margin: '0 auto',
})

const LogoWrapper = styled(Link, {
  width: 'fit-content',
  borderRadius: '$r1',
  '&:focus-visible': {
    outline: '$focusBlue solid 3px;',
    outlineOffset: '8px',
  },
})

const StyledNav = styled('nav', {
  height: '100%',
})

const NavigationWrap = styled('ul', {
  height: '100%',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gridAutoFlow: 'column',
  gap: '$48',
  display: 'none',
  '@lg': {
    display: 'grid',
  },
})

const MobileNav = styled(Button, {
  display: 'grid',
  justifyItems: 'flex-end',
  alignItems: 'center',
  projectFont: 'body03',
  color: 'currentColor',
  '@lg': {
    display: 'none',
  },
})

const NavigationItemWrapper = styled('li', {
  display: 'grid',
  gridAutoFlow: 'column',
  alignItems: 'center',
})

const NavigationItemIcon = styled('span', {
  width: '$20',
  height: '$20',
  display: 'grid',
  alignSelf: 'center',
  margin: '$0 $6 0 0',
})

const StyledLinkWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
})

const StyledLink = styled(Link, {
  color: 'currentcolor',
  opacity: 0.5,
  transition: '0.2s opacity',

  '@lg': {
    '&:hover': {
      opacity: 1,
    },
  },
  '&:active': {
    opacity: 1,
  },
  '&:focus-visible': {
    outline: '$focusBlue solid 3px;',
  },
  variants: {
    highlighted: {
      true: {
        '&::before': {
          content: '',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '$successGreen',
          alignSelf: 'center',
          marginRight: 10,
          marginBottom: 2,
        },
      },
    },
    currentPage: {
      true: {
        opacity: 1,

        '& ~ [data-chevron="nested-navigation-chevron"]': {
          opacity: 1,
        },
      },
    },
    isOpen: {
      true: {
        opacity: 1,
        '& ~ [data-chevron="nested-navigation-chevron"]': {
          opacity: 1,
          transform: 'rotateX(0deg)',
          transition: 'transform 0.2s ease-out',
        },
      },
      false: {
        '& ~[data-chevron="nested-navigation-chevron"]': {
          transition: 'transform 0.2s ease-out',
        },
      },
    },
  },
})

const ToggleSubmenuButton = styled(Button, {
  display: 'flex',
  cursor: 'pointer',
  alignSelf: 'center',
  color: 'currentcolor',
  transform: 'rotateX(180deg)',
  opacity: 0.5,
  transition: '0.2s opacity',

  variants: {
    theme: {
      'transparent-dark': {
        '& svg path': {
          stroke: '$black',
        },
      },
      'transparent-light': {
        '& svg path': {
          stroke: '$white',
        },
      },
    },
  },
})

export const Header = ({ header, theme, position, announcementBar }: Props) => {
  const router = useRouter()
  const { t } = useTranslation('header')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeNestedNavIndex, setActiveNestedNavIndex] = useState<
    number | null
  >(null)
  const HOVER_CLOSE_TIMEOUT = 150
  const hoverTimeoutRef = useRef<number | null>(null)
  const navigationRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      // close mobile nav
      setIsMobileMenuOpen(false)
      // close desktop nested nav
      startTransition(() => {
        setActiveNestedNavIndex(null)
      })
    }

    router.events.on('routeChangeComplete', handleRouteChangeComplete)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [setIsMobileMenuOpen, isMobileMenuOpen, router.events])

  const onMouseEnter = (index: number) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setActiveNestedNavIndex(index)
  }

  const onMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null

    if (
      relatedTarget?.nodeType === Node.ELEMENT_NODE &&
      navigationRef?.current?.contains(relatedTarget)
    ) {
      return
    }

    const timeout = window.setTimeout(() => {
      setActiveNestedNavIndex(null)
    }, HOVER_CLOSE_TIMEOUT)
    hoverTimeoutRef.current = timeout
  }

  const onIconClick = (index: number) => {
    setActiveNestedNavIndex(activeNestedNavIndex === index ? null : index)
  }

  return (
    <StyledWrapper>
      {announcementBar && (
        <AnnouncementBar announcementBar={announcementBar} theme={theme} />
      )}
      <StyledContainer position={position} theme={theme}>
        <NavContainer>
          <LogoWrapper appearance="grid" href="/" title="Commerce-ui logo">
            <Logo theme={theme} />
          </LogoWrapper>
          <StyledNav>
            <NavigationWrap>
              {header?.navLinks &&
                header?.navLinks?.length > 0 &&
                header?.navLinks?.map((navItem, index) =>
                  navItem._type === 'nestedNavigation' ? (
                    <NestedNavigationItem
                      key={navItem._key}
                      navItem={navItem}
                      theme={theme}
                      isOpen={activeNestedNavIndex === index}
                      ref={navigationRef}
                      onMouseEnter={() => onMouseEnter(index)}
                      onMouseLeave={onMouseLeave}
                      onIconClick={() => onIconClick(index)}
                      onSubmenuClose={() => setActiveNestedNavIndex(null)}
                    />
                  ) : (
                    <LinkNavigationItem
                      key={navItem._key}
                      navItem={navItem}
                      isOpen={activeNestedNavIndex === index}
                    />
                  ),
                )}
            </NavigationWrap>
          </StyledNav>
        </NavContainer>
        <MobileNav
          appearance="text"
          onClick={() => {
            setIsMobileMenuOpen(true)
          }}
        >
          {t('menu')}
        </MobileNav>
        <Modal
          preventScroll={true}
          isOpen={isMobileMenuOpen}
          onRequestClose={() => setIsMobileMenuOpen(false)}
          size={'stretch'}
        >
          <MobileDrawer
            onCloseClick={() => setIsMobileMenuOpen(false)}
            header={header}
            theme="transparent-light"
          />
        </Modal>
      </StyledContainer>
    </StyledWrapper>
  )
}

const LinkNavigationItem = ({
  navItem,
  isOpen,
}: {
  navItem: NavigationLinkItem
  isOpen: boolean
}) => {
  const router = useRouter()
  return (
    <NavigationItemWrapper>
      {navItem.ctaLabel && navItem?.ctaLink && (
        <StyledLinkWrapper>
          <StyledLink
            className="navigation-link"
            highlighted={navItem.highlighted}
            currentPage={
              (isLinkHref(navItem.ctaLink) &&
                navItem.ctaLink.href &&
                router.asPath.startsWith(navItem.ctaLink.href)) ||
              router.asPath === '/'
            }
            isOpen={isOpen}
            appearance="grid"
            {...navItem.ctaLink}
          >
            {navItem.ctaIcon?.mediaPayload &&
              (navItem.ctaIcon.mediaPayload as SanityImageMediaPayload).image
                .asset && (
                <NavigationItemIcon>
                  <Media {...navItem.ctaIcon} priority={false} sizes={'40px'} />
                </NavigationItemIcon>
              )}
            {navItem.ctaLabel}
          </StyledLink>
        </StyledLinkWrapper>
      )}
    </NavigationItemWrapper>
  )
}

type NestedNavigationItemProps = {
  navItem: NavigationNestedItem
  theme: 'transparent-dark' | 'transparent-light'
  isOpen: boolean
  onMouseEnter: () => void
  onMouseLeave: (e: React.MouseEvent) => void
  onIconClick: () => void
  onSubmenuClose: () => void
}

const NestedNavigationItem = forwardRef<
  HTMLLIElement,
  NestedNavigationItemProps
>(
  (
    {
      navItem,
      theme,
      isOpen,
      onMouseEnter,
      onMouseLeave,
      onIconClick,
      onSubmenuClose,
    },
    ref,
  ) => {
    const router = useRouter()
    const { t } = useTranslation('header')

    return (
      <NavigationItemWrapper
        ref={ref}
        className={'nested-navigation-link'}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {navItem.ctaLabel && navItem?.ctaLink && (
          <StyledLinkWrapper>
            <StyledLink
              className="navigation-link"
              currentPage={
                (isLinkHref(navItem.ctaLink) &&
                  navItem.ctaLink.href &&
                  router.asPath.startsWith(navItem.ctaLink.href)) ||
                router.asPath === '/'
              }
              isOpen={isOpen}
              appearance="grid"
              {...navItem.ctaLink}
              aria-expanded={isOpen}
            >
              {navItem.ctaIcon?.mediaPayload &&
                (navItem.ctaIcon.mediaPayload as SanityImageMediaPayload).image
                  .asset && (
                  <NavigationItemIcon>
                    <Media
                      {...navItem.ctaIcon}
                      priority={false}
                      sizes={'40px'}
                    />
                  </NavigationItemIcon>
                )}
              {navItem.ctaLabel}
            </StyledLink>
            <ToggleSubmenuButton
              theme={theme}
              data-chevron="nested-navigation-chevron"
              onClick={onIconClick}
              aria-label={t('nestedNavButtonLabel', {
                label: navItem.ctaLabel,
              })}
              aria-expanded={isOpen}
              aria-controls={`nested-nav-${navItem._key}`}
              type="button"
            >
              <Chevron />
            </ToggleSubmenuButton>
          </StyledLinkWrapper>
        )}
        <NestedNavigationPanel
          id={`nested-nav-${navItem._key}`}
          navItem={navItem}
          theme={theme}
          isOpen={isOpen}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClose={onSubmenuClose}
        />
      </NavigationItemWrapper>
    )
  },
)
